import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { UserContext } from "../../context/UserContext";
import {
  ShieldCheck,
  Phone,
  PhoneOff,
  Mail,
  BellRing,
  BellOff,
  Save,
} from "lucide-react";
import axios from "axios";
import { getHeader } from "../../../utility/utility";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../../constants/constants";

const PrivacySettings = () => {
  const { user, loading, reloadUser } = useContext(UserContext);

  // Local state for toggles
  const [contactVisibility, setContactVisibility] = useState(false);
  const [emailVisibility, setEmailVisibility] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(true);

  // UI states
  const [isUpdating, setIsUpdating] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Sync state when user data is available
  useEffect(() => {
    if (user) {
      setContactVisibility(user.contactVisibility ?? false);
      setEmailVisibility(user.emailVisibility ?? false);
      setShowPrivacyPopup(user.showPrivacyPopup ?? true);
    }
  }, [user]);

  const handleUpdate = async () => {
    setIsUpdating(true);
    setSuccessMessage("");

    try {
      const response = await axios.put(
        `${API_ENDPOINT}/user/update-privacy-settings`,
        {
          contactVisibility,
          emailVisibility,
          showPrivacyPopup,
        },
        getHeader()
      );
      toast.success(response.data.message);
      setSuccessMessage("Privacy settings updated successfully!");
      reloadUser();
    } catch (error) {
      console.error("Error updating privacy settings:", error);
    } finally {
      setIsUpdating(false);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header Section */}
          <Box display="flex" alignItems="center" mb={1}>
            <ShieldCheck
              size={32}
              color="#1976d2"
              style={{ marginRight: "12px" }}
            />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Privacy Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" mb={4}>
            Manage how your information is shared and displayed across the
            platform.
          </Typography>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}

          {/* Contact Visibility Setting */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={3}
          >
            <Box display="flex" alignItems="flex-start" pr={2}>
              <Box
                mt={0.5}
                mr={2}
                color={contactVisibility ? "primary.main" : "text.disabled"}
              >
                {contactVisibility ? (
                  <Phone size={24} />
                ) : (
                  <PhoneOff size={24} />
                )}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Contact Visibility
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  When enabled, exhibitors and other users will be able to see
                  your contact number. Turn this off to keep your contact
                  details private.
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={contactVisibility}
              onChange={(e) => setContactVisibility(e.target.checked)}
              color="primary"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Email Visibility Setting */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={3}
          >
            <Box display="flex" alignItems="flex-start" pr={2}>
              <Box
                mt={0.5}
                mr={2}
                color={emailVisibility ? "primary.main" : "text.disabled"}
              >
                <Mail size={24} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Email Visibility
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  When enabled, your email address will be visible to exhibitors
                  and other delegates. Turn this off to hide your email.
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={emailVisibility}
              onChange={(e) => setEmailVisibility(e.target.checked)}
              color="primary"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Privacy Reminder Setting */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={4}
          >
            <Box display="flex" alignItems="flex-start" pr={2}>
              <Box
                mt={0.5}
                mr={2}
                color={showPrivacyPopup ? "primary.main" : "text.disabled"}
              >
                {showPrivacyPopup ? (
                  <BellRing size={24} />
                ) : (
                  <BellOff size={24} />
                )}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Privacy Settings Reminder
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Show a popup reminder on the dashboard if your contact
                  visibility is disabled. Turn this off if you no longer wish to
                  see the prompt.
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={showPrivacyPopup}
              onChange={(e) => setShowPrivacyPopup(e.target.checked)}
              color="primary"
            />
          </Box>

          {/* Action Buttons */}
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleUpdate}
              disabled={isUpdating}
              startIcon={
                isUpdating ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Save size={20} />
                )
              }
              disableElevation
              sx={{ textTransform: "none", fontWeight: 600, px: 4 }}
            >
              {isUpdating ? "Saving..." : "Update Settings"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PrivacySettings;
